// Vista ANDÓN: tablero de pared para la cocina. Se lee a 5 metros, sin interacción.
// Lo usan dos pantallas: /catering/andon (dentro del ERP) y /cocina/:token (TV pública).
// Regla de los tres niveles: franja global (8 m) → tarjetas (5 m) → detalle (1 m).
import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Flame, Snowflake, Thermometer } from 'lucide-react';
import {
  ESTADO_GLOBAL_CLS, ESTADO_GLOBAL_LABEL, PISO_ESTADO_LABEL, PisoTablero,
  SEMAFORO_CLS, hhmm, holguraTxt,
} from './shared';

const POR_PAGINA = 12;        // más de 12 tarjetas no se leen: se pagina
const SEG_PAGINA = 15;        // rotación automática

export function AndonVista({ t, titulo }: { t: PisoTablero; titulo?: string }) {
  const [pagina, setPagina] = useState(0);
  const [reloj, setReloj] = useState(() => new Date());

  useEffect(() => {
    const i = setInterval(() => setReloj(new Date()), 1000);
    return () => clearInterval(i);
  }, []);

  // Solo lo que está vivo; lo despachado se muestra en el marcador, no en tarjetas
  const vivos = useMemo(() => t.batches.filter((b) => b.estado !== 'despachado'), [t.batches]);
  const paginas = Math.max(1, Math.ceil(vivos.length / POR_PAGINA));

  useEffect(() => {
    if (paginas <= 1) { setPagina(0); return; }
    const i = setInterval(() => setPagina((p) => (p + 1) % paginas), SEG_PAGINA * 1000);
    return () => clearInterval(i);
  }, [paginas]);

  const visibles = vivos.slice(pagina * POR_PAGINA, pagina * POR_PAGINA + POR_PAGINA);
  const r = t.resumen;
  const mins = r.minutos_al_servicio;
  const cuentaAtras = mins == null ? '—'
    : mins < 0 ? `SERVICIO PASADO ${holguraTxt(mins)}`
    : mins >= 60 ? `T− ${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, '0')}m`
    : `T− ${mins} min`;
  const pctDespacho = Number(r.porciones_plan) > 0
    ? Math.round((Number(r.porciones_despachadas) / Number(r.porciones_plan)) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#12151a] text-white p-4 space-y-3 select-none">
      {/* NIVEL 1 — se lee a 8 metros */}
      <div className={`rounded-xl px-6 py-3 flex items-center justify-between gap-4 ${ESTADO_GLOBAL_CLS[r.estado_global]}`}>
        <div>
          <div className="text-4xl xl:text-5xl font-black tracking-tight">{ESTADO_GLOBAL_LABEL[r.estado_global]}</div>
          <div className="text-sm opacity-90">{titulo ?? 'Cocina central'} · {r.fecha}</div>
        </div>
        <div className="text-center">
          <div className="text-5xl xl:text-6xl font-black font-mono tabular-nums">{cuentaAtras}</div>
          <div className="text-sm opacity-90">
            próximo servicio {hhmm(r.proximo_servicio)}
          </div>
        </div>
        <div className="text-right">
          <div className="text-4xl xl:text-5xl font-black font-mono tabular-nums">
            {String(reloj.getHours()).padStart(2, '0')}:{String(reloj.getMinutes()).padStart(2, '0')}
          </div>
          <div className="text-sm opacity-90">{paginas > 1 ? `página ${pagina + 1}/${paginas}` : `${vivos.length} en curso`}</div>
        </div>
      </div>

      {/* Marcador de porciones + contadores */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        {[
          { l: 'Porciones', v: `${Math.round(Number(r.porciones_despachadas))} / ${Math.round(Number(r.porciones_plan))}`, s: `${pctDespacho}% despachado` },
          { l: 'Despachados', v: r.despachados, s: `de ${r.total_batches} batches` },
          { l: 'En cocción', v: r.en_curso, s: 'en proceso ahora' },
          { l: 'Pendientes', v: r.pendientes, s: 'sin empezar' },
          { l: 'En rojo', v: r.en_rojo, s: 'no llegan a tiempo', alerta: r.en_rojo > 0 },
          { l: 'Detenidos', v: r.detenidos, s: 'esperan algo', alerta: r.detenidos > 0 },
        ].map((k) => (
          <div key={k.l} className={`rounded-lg px-3 py-2 ${k.alerta ? 'bg-red-900/40 ring-2 ring-red-500' : 'bg-white/5'}`}>
            <div className="text-xs uppercase tracking-wide text-white/60">{k.l}</div>
            <div className="text-2xl xl:text-3xl font-bold font-mono tabular-nums">{k.v}</div>
            <div className="text-xs text-white/50">{k.s}</div>
          </div>
        ))}
      </div>

      {/* Barra de avance del día */}
      <div className="h-3 rounded-full bg-white/10 overflow-hidden">
        <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pctDespacho}%` }} />
      </div>

      {/* Alertas: máximo 3, la más grave arriba */}
      {t.alertas.length > 0 && (
        <div className="space-y-1">
          {t.alertas.slice(0, 3).map((a) => (
            <div key={a.batch_id + a.tipo}
              className={`flex items-center gap-2 rounded-lg px-4 py-2 text-lg xl:text-xl font-semibold ${
                a.tipo === 'sanitario' ? 'bg-sky-600' : a.tipo === 'detenido' ? 'bg-purple-700' : 'bg-red-700'}`}>
              <AlertTriangle className="w-6 h-6 shrink-0" /> {a.texto}
            </div>
          ))}
        </div>
      )}

      {t.cuello && (
        <div className="rounded-lg bg-amber-600 px-4 py-2 text-lg font-semibold">
          CUELLO: {t.cuello.equipo} — {t.cuello.demanda} batches para {t.cuello.unidades} unidad(es)
        </div>
      )}

      {/* NIVEL 2 — tarjetas de batch, se leen a 5 metros */}
      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
        {visibles.map((b) => (
          <div key={b.id}
            className={`rounded-xl border-4 overflow-hidden ${SEMAFORO_CLS[b.semaforo]} text-black dark:text-white
                        ${b.semaforo === 'rojo' ? 'animate-pulse' : ''} ${b.es_critico ? 'ring-4 ring-yellow-400' : ''}`}>
            <div className="px-3 py-1.5 flex items-center justify-between bg-black/10 dark:bg-white/10">
              <span className="font-mono text-sm">{b.codigo}{b.sub_total > 1 ? ` (${b.sub_indice}/${b.sub_total})` : ''}</span>
              <span className="text-sm font-semibold">{hhmm(b.hora_servicio)}</span>
            </div>
            <div className="px-3 py-2">
              <div className="text-2xl xl:text-3xl font-bold leading-tight">
                {b.es_critico && '👑 '}{b.receta}
              </div>
              <div className="text-sm opacity-80">{b.estacion}{b.equipo ? ` · ${b.equipo}` : ''}</div>
              <div className="flex items-baseline justify-between pt-1">
                <span className="text-3xl xl:text-4xl font-black font-mono tabular-nums">
                  {Math.round(Number(b.porciones_reales ?? b.porciones_plan))}
                </span>
                <span className="text-xl font-bold font-mono tabular-nums">
                  {b.estado === 'planificado' ? `inicia ${hhmm(b.latest_start)}` : holguraTxt(b.holgura_min)}
                </span>
              </div>
            </div>
            <div className="px-3 py-1 flex flex-wrap items-center gap-2 text-xs bg-black/5 dark:bg-white/5">
              <span className="font-bold uppercase">{PISO_ESTADO_LABEL[b.estado] ?? b.estado}</span>
              {b.responsable && <span className="opacity-70">· {b.responsable}</span>}
              {b.temperatura_c != null && (
                <span className="inline-flex items-center gap-0.5"><Thermometer className="w-3 h-3" />{Number(b.temperatura_c).toFixed(0)}°</span>
              )}
              {b.peligro_min != null && b.peligro_semaforo !== 'ok' && (
                <span className={`inline-flex items-center gap-0.5 font-bold ${b.peligro_semaforo === 'vencido' ? 'text-red-700' : 'text-amber-700'}`}>
                  {b.peligro_semaforo === 'vencido' ? <Snowflake className="w-3 h-3" /> : <Flame className="w-3 h-3" />}
                  {Math.round(b.peligro_min)} min
                </span>
              )}
              {b.es_dieta && <span className="rounded-full bg-fuchsia-600 px-2 py-0.5 font-bold text-white">DIETA</span>}
              {(b.alergenos ?? []).map((a) => (
                <span key={a} className="rounded-full bg-red-600 px-2 py-0.5 font-bold text-white">{a}</span>
              ))}
            </div>
            {b.motivo && <div className="px-3 py-1 text-sm font-semibold bg-purple-700 text-white">⛔ {b.motivo}</div>}
          </div>
        ))}
        {!vivos.length && (
          <div className="col-span-full py-24 text-center text-3xl text-white/50">
            Todo despachado — servicio completo 👨‍🍳
          </div>
        )}
      </div>
    </div>
  );
}
